import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { formatDateAbre } from '../../Components/Funcionais/Utils';
import { useEffect, useState } from "react";
import APIAxios from "../../Api/APIAxios";
import SessaoAuth from "../../Hooks/SessaoAuth";
import { compareAsc } from 'date-fns';

const URL_ProdDia = "/produtividade/dia";

const ProdutividadeDia = () => {

    const apiAxios = APIAxios();
    const { sessao } = SessaoAuth();

    const [loadingProdDia, setLoadingProdDia] = useState(false);
    const [dataProdDia, setDataProdDia] = useState([]);
    const [error, setError] = useState("");
    const [datas, setDatas] = useState([]);

    const loadProdDia = async () => {

        setLoadingProdDia(true);
        setError("");

        try {

            const response = await apiAxios.post(URL_ProdDia,
                {
                    data_inicio: sessao.dataIni,
                    data_fim: sessao.dataFim,
                    id_usuario: 0,
                });

            setDataProdDia(response.data.data);

        } catch (err) {
            if (!err.response) {
                setError("Servidor não responde. Verifique informações selecionadas.");
            } else if (err.response.status === 400 || err.response.status === 401) {
                setError(err.response.data.erro);
            } else {
                setError("Erro desconhecido.");
            }
        } finally {
            setLoadingProdDia(false);
        }
    };

    useEffect(() => {
        loadProdDia();
    }, [sessao]);

    const sumarizar = async () => {

        setLoadingProdDia(true);
        setError("");

        const grouped = [...dataProdDia].reduce((acc, item) => {
            if (!acc[item.data]) {
                acc[item.data] = { totalProd: 0, count: 0 };
            }
            acc[item.data].totalProd += item.minRegistrados * 100 / item.minTotal;
            acc[item.data].count += 1;
            return acc;
        }, {});

        const resultado = Object.keys(grouped).map(data => ({
            data,
            produtividade: grouped[data].totalProd / grouped[data].count > 100 ? 100 : grouped[data].totalProd / grouped[data].count
        }));

        const sorted = resultado.sort((a, b) => {
            return compareAsc(a.data, b.data);
        });

        setDatas(sorted);

        setLoadingProdDia(false);
    };

    useEffect(() => {
        sumarizar();
    }, [dataProdDia]);

    const chartOptionsMult = {
        chart: {
            type: "bar",
            height: 350,
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#647DEE"],
        fill: {
            type: "gradient",
            gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.3,
                gradientToColors: ["#1A3865"],
                inverseColors: false,
                opacityFrom: 1,
                opacityTo: 0.85,
            },
        },
        stroke: {
            width: [0],
        },
        plotOptions: {
            bar: {
                borderRadius: 8,
                dataLabels: {
                    position: "top",
                },
                columnWidth: "65%",
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (val) => `${val.toFixed(0)}%`,
            style: {
                fontSize: "10px",
                fontWeight: 700,
                colors: ["#1A3865"],
            },
            offsetY: -4,
        },
        grid: {
            borderColor: "#e0e7ff",
            strokeDashArray: 4,
            padding: { top: 0, right: 10, bottom: 0, left: 10 },
        },
        xaxis: {
            categories: datas.map((item) => `${formatDateAbre(item.data)}`),
            position: "bottom",
            axisBorder: { show: false },
            axisTicks: { show: false },
            tooltip: { enabled: false },
            labels: {
                style: {
                    fontSize: "10px",
                    colors: "#6b7a99",
                    fontWeight: 500,
                },
            },
        },
        yaxis: [
            {
                labels: {
                    formatter: (val) => `${val.toFixed(0)}%`,
                    style: { colors: "#6b7a99", fontSize: "11px" },
                },
                title: {
                    text: "Aproveitamento",
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#1A3865",
                        fontFamily: "inherit",
                    },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
        ],
        tooltip: {
            theme: "light",
            y: { formatter: (val) => `${val.toFixed(1)}%` },
        },
        title: {
            text: "",
        },
    };

    return (
        <Box sx={{ width: "100%", mt: 4, position: "relative" }}>
            <Card sx={{
                width: "100%",
                boxShadow: "0 8px 28px rgba(26, 56, 101, 0.12), 0 2px 6px rgba(100,125,238,0.08)",
                borderRadius: "16px",
                border: "1px solid rgba(100, 125, 238, 0.15)",
                overflow: "hidden",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: "linear-gradient(90deg, #269FFB, #647DEE, #1A3865)",
                },
            }}>
                <CardContent sx={{ pt: 3 }}>
                    <Typography gutterBottom align="center" sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#1A3865",
                        letterSpacing: "0.4px",
                    }}>
                        Aproveitamento por Dia
                    </Typography>
                    {loadingProdDia || !datas || datas.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress
                                size={40}
                                sx={{ color: "#647DEE" }}
                            />
                        </Box>
                    ) : (
                        <ReactApexChart
                            options={chartOptionsMult}
                            series={[
                                {
                                    name: "Aproveitamento",
                                    type: "column",
                                    data: datas ? datas.map((item) => item.produtividade) : [],
                                },
                            ]}
                            type="bar"
                            height={350}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default ProdutividadeDia;
